import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Grid, Button } from "@mui/material";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import "../../styles/scss/trialAnalytics.scss";

const darkTheme = {
  background: "#111827",
  paper: "#1f2937",
  border: "#374151",
  text: "#f9fafb",
  subText: "#9ca3af",
  grid: "#2d3748",
  tooltip: "rgba(31, 41, 55, 0.97)",
};

const cardSx = {
  bgcolor: darkTheme.paper,
  border: `1px solid ${darkTheme.border}`,
  borderRadius: 3,
  boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
};

const COLUMNS = [
  { key: "total_unique_trials",   label: "体験者数",       numeric: true },
  { key: "new_trialers",          label: "新規体験者数",   numeric: true },
  { key: "new_joined",            label: "新規入会者数",   numeric: true },
  { key: "conversion_rate",       label: "新規入会率",     numeric: true },
  { key: "new_not_joined",        label: "新規未入会",     numeric: true },
  { key: "existing_member_trials",label: "既存会員体験者数", numeric: true },
];

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) {
    return <span style={{ opacity: 0.3, marginLeft: 4, fontSize: 10 }}>⇅</span>;
  }
  return (
    <span style={{ marginLeft: 4, fontSize: 10, color: "#38bdf8" }}>
      {sortDir === "asc" ? "▲" : "▼"}
    </span>
  );
};

function TrialAnalyticsPage() {
  useLoginCheck();

  const getMonthValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const getDefaultStartMonth = () => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() - 5);
    return getMonthValue(date);
  };

  const getDefaultEndMonth = () => {
    const date = new Date();
    return getMonthValue(date);
  };

  const buildMonthOptions = (startDate, endDate) => {
    if (!startDate || !endDate) return [];
    const [startY, startM] = startDate.split("-").map(Number);
    const [endY, endM] = endDate.split("-").map(Number);
    if (!startY || !startM || !endY || !endM) return [];
    const options = [];
    const cursor = new Date(startY, startM - 1, 1);
    const end = new Date(endY, endM - 1, 1);
    while (cursor <= end) {
      const year = cursor.getFullYear();
      const month = String(cursor.getMonth() + 1).padStart(2, "0");
      options.push(`${year}-${month}`);
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return options;
  };

  const minMonth = "2022-09";
  const maxMonth = getDefaultEndMonth();
  const defaultStart = getDefaultStartMonth();
  const initialStart = defaultStart < minMonth ? minMonth : defaultStart > maxMonth ? maxMonth : defaultStart;
  const initialEnd = maxMonth < initialStart ? initialStart : maxMonth;
  const monthOptions = buildMonthOptions(`${minMonth}-01`, `${maxMonth}-01`);

  const [trialData, setTrialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startMonth, setStartMonth] = useState(initialStart);
  const [endMonth, setEndMonth] = useState(initialEnd);
  const [rangeError, setRangeError] = useState("");
  const [breakdownTab, setBreakdownTab] = useState("store");
  const [sortField, setSortField] = useState("conversion_rate");
  const [sortDir, setSortDir] = useState("desc");

  // タブ切り替え時にソートをデフォルトに戻す
  useEffect(() => {
    setSortField("conversion_rate");
    setSortDir("desc");
  }, [breakdownTab]);

  const fetchTrialData = async (targetStart, targetEnd, signal) => {
    setLoading(true);
    setError(null);
    setRangeError("");
    try {
      if (!targetStart || !targetEnd) {
        setRangeError("開始月と終了月を選択してください。");
        return;
      }
      if (targetStart > targetEnd) {
        setRangeError("開始月が終了月より後になっています。");
        return;
      }
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await fetch(
        `${apiUrl}/api/get_trial_store_analytics/?start_month=${targetStart}&end_month=${targetEnd}`,
        { signal }
      );
      if (!res.ok) throw new Error("ネットワークエラー");
      const json = await res.json();
      setTrialData(json);
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error("店舗別体験分析の取得エラー:", e);
        setError(e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchTrialData(startMonth, endMonth, controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box sx={{ bgcolor: darkTheme.background, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: darkTheme.subText }}>読み込み中...</Typography>
      </Box>
    );
  }

  const trialSummary = trialData?.summary ?? {
    total_unique_trials: 0,
    new_trialers: 0,
    new_joined: 0,
    new_not_joined: 0,
    existing_member_trials: 0,
    conversion_rate: 0,
  };
  const trialStores = trialData?.stores ?? [];
  const trialRange = trialData?.range ?? null;
  const generatedAt = trialData?.generated_at ?? null;
  const rangeLabel = trialRange ? `${trialRange.start}〜${trialRange.end}` : "—";
  const formatRate = (rate) => `${(rate * 100).toFixed(1)}%`;
  const handlePrint = () => window.print();
  const handleApply = () => {
    const controller = new AbortController();
    fetchTrialData(startMonth, endMonth, controller.signal);
  };
  const hasMonthOptions = monthOptions.length > 0;
  const visibleStartOptions = monthOptions;
  const visibleEndOptions = monthOptions;

  const isStore = breakdownTab === "store";
  const nameKey = isStore ? "studio" : "instructor";
  const colLabel = isStore ? "店舗" : "インストラクター";
  const rows = isStore ? trialStores : (trialData?.instructors ?? []);

  // ソート処理（合計行は常に末尾）
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      // 名前列は昇順スタート、数値列は降順スタート
      setSortDir(field === nameKey ? "asc" : "desc");
    }
  };

  const sortedRows = [...rows].sort((a, b) => {
    if (sortField === nameKey) {
      const cmp = (a[nameKey] ?? "").localeCompare(b[nameKey] ?? "", "ja");
      return sortDir === "asc" ? cmp : -cmp;
    }
    const av = a[sortField] ?? 0;
    const bv = b[sortField] ?? 0;
    return sortDir === "asc" ? av - bv : bv - av;
  });

  const summaryRow = isStore
    ? { ...trialSummary, [nameKey]: "合計", isSummary: true }
    : (() => {
        const totals = rows.reduce(
          (acc, r) => ({
            total_unique_trials: acc.total_unique_trials + (r.total_unique_trials ?? 0),
            new_trialers: acc.new_trialers + (r.new_trialers ?? 0),
            new_joined: acc.new_joined + (r.new_joined ?? 0),
            new_not_joined: acc.new_not_joined + (r.new_not_joined ?? 0),
            existing_member_trials: acc.existing_member_trials + (r.existing_member_trials ?? 0),
          }),
          { total_unique_trials: 0, new_trialers: 0, new_joined: 0, new_not_joined: 0, existing_member_trials: 0 }
        );
        return {
          ...totals,
          conversion_rate: totals.new_trialers > 0 ? totals.new_joined / totals.new_trialers : 0,
          [nameKey]: "合計",
          isSummary: true,
        };
      })();

  const thStyle = (field) => ({
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    background: sortField === field ? "rgba(56,189,248,0.08)" : "transparent",
    transition: "background 0.15s",
  });

  return (
    <Box className="trial-analytics-page" sx={{ bgcolor: darkTheme.background, minHeight: "100vh", p: { xs: 2, md: 3 } }}>
      <Box className="trial-store-section print-area">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { md: "center" },
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ color: darkTheme.text }}>
              入会率分析
            </Typography>
            <Typography sx={{ color: darkTheme.subText, mt: 0.5, fontSize: 13 }}>
              対象期間: {rangeLabel}
            </Typography>
            <Typography className="print-only" sx={{ color: darkTheme.subText, mt: 0.5, fontSize: 12 }}>
              出力日: {generatedAt || "—"}
            </Typography>
          </Box>
          <Button
            variant="contained"
            className="no-print"
            onClick={handlePrint}
            sx={{
              bgcolor: "#0ea5e9",
              color: "#0b1020",
              fontWeight: 700,
              "&:hover": { bgcolor: "#0284c7" },
              alignSelf: { xs: "flex-start", md: "center" },
            }}
          >
            PDF出力
          </Button>
        </Box>

        <Box
          className="no-print"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: darkTheme.subText, fontSize: 12 }}>開始月</Typography>
            <select
              value={startMonth}
              onChange={(e) => {
                const next = e.target.value;
                setStartMonth(next);
                if (endMonth < next) {
                  setEndMonth(next);
                }
              }}
              className="trial-month-select"
              disabled={!hasMonthOptions}
            >
              {!hasMonthOptions && <option value="">—</option>}
              {visibleStartOptions.map((month) => (
                <option value={month} key={month} disabled={endMonth && month > endMonth}>
                  {month}
                </option>
              ))}
            </select>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: darkTheme.subText, fontSize: 12 }}>終了月</Typography>
            <select
              value={endMonth}
              onChange={(e) => {
                const next = e.target.value;
                setEndMonth(next);
                if (startMonth > next) {
                  setStartMonth(next);
                }
              }}
              className="trial-month-select"
              disabled={!hasMonthOptions}
            >
              {!hasMonthOptions && <option value="">—</option>}
              {visibleEndOptions.map((month) => (
                <option value={month} key={month} disabled={startMonth && month < startMonth}>
                  {month}
                </option>
              ))}
            </select>
          </Box>
          <Button
            variant="outlined"
            className="trial-filter-button"
            onClick={handleApply}
            disabled={loading || !hasMonthOptions}
            sx={{
              borderColor: "#38bdf8",
              color: "#e2e8f0",
              fontWeight: 600,
              "&:hover": { borderColor: "#7dd3fc", background: "rgba(56,189,248,0.08)" },
            }}
          >
            絞り込み
          </Button>
          <Typography sx={{ color: darkTheme.subText, fontSize: 11 }}>
            絞り込みボタンで再集計されます
          </Typography>
        </Box>

        {!hasMonthOptions && (
          <Typography sx={{ color: darkTheme.subText, fontSize: 12, mb: 2 }}>
            体験データがまだありません。
          </Typography>
        )}

        {rangeError && (
          <Typography sx={{ color: "#fca5a5", mb: 2 }}>{rangeError}</Typography>
        )}

        {error ? (
          <Typography sx={{ color: "#fca5a5" }}>データの取得に失敗しました。</Typography>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ ...cardSx, borderTop: "3px solid #38bdf8" }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography sx={{ color: darkTheme.subText, fontSize: 12 }}>体験者数（ユニーク）</Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: darkTheme.text, mt: 0.5 }}>
                      {(trialSummary.total_unique_trials ?? 0).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ ...cardSx, borderTop: "3px solid #22c55e" }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography sx={{ color: darkTheme.subText, fontSize: 12 }}>新規体験者数</Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: "#86efac", mt: 0.5 }}>
                      {(trialSummary.new_trialers ?? 0).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ ...cardSx, borderTop: "3px solid #eab308" }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography sx={{ color: darkTheme.subText, fontSize: 12 }}>新規入会者数</Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: "#facc15", mt: 0.5 }}>
                      {(trialSummary.new_joined ?? 0).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ ...cardSx, borderTop: "3px solid #f97316" }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography sx={{ color: darkTheme.subText, fontSize: 12 }}>既存会員体験者数</Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: "#fdba74", mt: 0.5 }}>
                      {(trialSummary.existing_member_trials ?? 0).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ ...cardSx, borderTop: "3px solid #a78bfa" }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography sx={{ color: darkTheme.subText, fontSize: 12 }}>新規入会率</Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: "#c4b5fd", mt: 0.5 }}>
                      {formatRate(trialSummary.conversion_rate ?? 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* タブ切り替え */}
            <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
              {[{ key: "store", label: "店舗別" }, { key: "instructor", label: "インストラクター別" }].map(({ key, label }) => (
                <Box
                  key={key}
                  onClick={() => setBreakdownTab(key)}
                  sx={{
                    px: 2,
                    py: 0.6,
                    borderRadius: 1.5,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    bgcolor: breakdownTab === key ? "#0ea5e9" : darkTheme.border,
                    color: breakdownTab === key ? "#fff" : darkTheme.subText,
                    transition: "all 0.15s",
                    "&:hover": { opacity: 0.8 },
                    userSelect: "none",
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>

            {rows.length === 0 ? (
              <Typography sx={{ color: darkTheme.subText }}>
                対象期間内の体験データがありません。
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <table className="trial-analytics-table">
                  <thead>
                    <tr>
                      <th
                        className="col-store"
                        style={thStyle(nameKey)}
                        onClick={() => handleSort(nameKey)}
                      >
                        {colLabel}
                        <SortIcon field={nameKey} sortField={sortField} sortDir={sortDir} />
                      </th>
                      {COLUMNS.map(({ key, label }) => (
                        <th
                          key={key}
                          style={thStyle(key)}
                          onClick={() => handleSort(key)}
                        >
                          {label}
                          <SortIcon field={key} sortField={sortField} sortDir={sortDir} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...sortedRows, summaryRow].map((row, idx) => (
                      <tr key={`${row[nameKey]}-${idx}`} className={row.isSummary ? "summary-row" : ""}>
                        <td className="col-store">{row[nameKey]}</td>
                        <td>{(row.total_unique_trials ?? 0).toLocaleString()}</td>
                        <td>{(row.new_trialers ?? 0).toLocaleString()}</td>
                        <td>{(row.new_joined ?? 0).toLocaleString()}</td>
                        <td>{formatRate(row.conversion_rate ?? 0)}</td>
                        <td>{(row.new_not_joined ?? 0).toLocaleString()}</td>
                        <td>{(row.existing_member_trials ?? 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export default TrialAnalyticsPage;
